import { sanitizeTrustFactors } from "./rubric";
import {
  TRUST_FACTOR_SOURCES,
  type TaskTrustFields,
  type TrustFactorsSource,
  type TrustGrade,
  type TrustGradeStatsRow,
} from "./types";

/** The trust columns of a tasks row. Optional: older selects do not ask for them. */
export interface TaskTrustRow {
  trust_grade?: number | null;
  trust_factors?: unknown;
  trust_factors_source?: string | null;
  trust_special_suggested?: boolean | null;
  trust_grade_override?: number | null;
}

/** True when an admin override is in force. The reason never leaves the admin-only events table. */
export function isTrustGradeOverridden(row: Pick<TaskTrustRow, "trust_grade_override">): boolean {
  return row.trust_grade_override !== null && row.trust_grade_override !== undefined;
}

/** Shared by the quest and admin services' mapTaskRow copies (the bounty cards use isTrustGradeOverridden). */
export function mapTaskTrustColumns(row: TaskTrustRow): Partial<TaskTrustFields> {
  if (row.trust_grade === undefined || row.trust_grade === null) return {};
  const source = (TRUST_FACTOR_SOURCES as readonly string[]).includes(row.trust_factors_source ?? "")
    ? (row.trust_factors_source as TrustFactorsSource)
    : "none";
  return {
    trustGrade: row.trust_grade as TrustGrade,
    trustFactors: sanitizeTrustFactors(row.trust_factors),
    trustFactorsSource: source,
    trustSpecialSuggested: row.trust_special_suggested === true,
    trustGradeOverridden: isTrustGradeOverridden(row),
  };
}

/** One row of public.admin_trust_grade_stats. Counts arrive as bigint strings, averages as numeric. */
export function mapTrustGradeStatsRow(r: Record<string, unknown>): TrustGradeStatsRow {
  const num = (v: unknown) => (v === null || v === undefined ? null : Number(v));
  return {
    grade: Number(r.grade) as TrustGrade,
    taskCount: Number(r.task_count),
    openCount: Number(r.open_count),
    inProgressCount: Number(r.in_progress_count),
    doneCount: Number(r.done_count),
    cancelledCount: Number(r.cancelled_count),
    disputedCount: Number(r.disputed_count),
    medianHoursToFirstProposal: num(r.median_hours_to_first_proposal),
    avgBudgetUsd: num(r.avg_budget_usd),
    creatorEditedCount: Number(r.creator_edited_count),
    heuristicCount: Number(r.heuristic_count),
    overriddenCount: Number(r.overridden_count),
    overridesUp: Number(r.overrides_up),
    overridesDown: Number(r.overrides_down),
  };
}
